<template>
    <div class="emr-appointments-modal">

        <b-btn size="sm"
               @click="showModal()">
            {{ $i18n.gt('LABEL_VIEW_APPOINTMENTS') }}
            <shs-icon name="calendar"></shs-icon>
        </b-btn>

        <!-- modal -->
        <b-modal size="xl"
                 body-class="emr-appointments-modal__modal"
                 :id="modal_id">
            <template v-slot:modal-header>
                <h5>{{ $i18n.gt('LABEL_VIEW_APPOINTMENTS') }}</h5>
                <div class="float-right">
                    <patient-id :participant="participant"></patient-id>
                </div>
            </template>
            <data-table ref="dataTable"
                        :show-filters="true"
                        :show-pager="true"
                        :show-details="true"
                        :get-items="getAppointments"
                        :filters="filters"
                        :fields="fields">

                <!-- custom template for extra header info -->
                <template v-slot:headerExtra>
                    <b-btn variant="primary"
                           size="sm"
                           class="float-right my-0 mr-2"
                           @click="setFilters('year')">
                        {{ $i18n.gt('LABEL_THIS_YEAR') }}
                    </b-btn>
                    <b-btn variant="primary"
                           size="sm"
                           class="float-right my-0 mr-2"
                           @click="setFilters('month')">
                        {{ $i18n.gt('LABEL_THIS_MONTH') }}
                    </b-btn>
                    <b-btn variant="primary"
                           size="sm"
                           class="float-right my-0 mr-2"
                           @click="setFilters('week')">
                        {{ $i18n.gt('LABEL_THIS_WEEK') }}
                    </b-btn>
                </template>

                <!-- custom template for filters -->
                <template v-slot:filters>
                    <b-row cols="3">
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_START_DATE')">
                                <b-form-input type="date"
                                              v-model="filters.start.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_END_DATE')">
                                <b-form-input type="date"
                                              v-model="filters.end.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_APPOINTMENT_STATUS')">
                                <b-form-select :options="statusOptions"
                                               v-model="filters.status.value"
                                               @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </template>

                <!-- template for custom columns -->
                <template v-slot:customField="slotProps">
                    <div v-if="slotProps.row.field.key === 'date'">
                        <timestamp :date="slotProps.row.item['start']"
                                   :includeDay="true"/>
                    </div>
                    <div v-else-if="(slotProps.row.field.key === 'start') || (slotProps.row.field.key === 'end')">
                        <timestamp :date="slotProps.row.item[slotProps.row.field.key]"
                                   :includeDate="false"
                                   :includeTime="true"/>
                    </div>
                    <div v-else-if="slotProps.row.field.key === 'comment'">
                        <div class="ellipsis">{{ slotProps.row.item[slotProps.row.field.key] }}</div>
                    </div>
                    <div v-else-if="slotProps.row.field.key === 'emr_status'">
                        {{ displayStatus(slotProps.row.item[slotProps.row.field.key]) }}
                    </div>
                </template>

                <!-- template for custom row details -->
                <template v-slot:customRowDetails="slotProps">
                    <span v-html="displayComment(slotProps.row.item.comment)"/>
                </template>
            </data-table>
            <template #modal-footer>
                <b-btn class="btn-ghost-blue float-right"
                       @click="hideModal()">
                    Dismiss
                </b-btn>
            </template>
        </b-modal>

    </div>
</template>

<script>

import PractitionerPatientMixin from '@/providers/PractitionerPatientMixin'
import moment from 'moment'

export default {
    name: 'EmrAppointmentsModal',
    mixins: [PractitionerPatientMixin],
    props: {
        participantId: Number
    },
    data() {
        return {
            DATE_FORMAT: 'YYYY-MM-DD',
            modal_id: "emrAppointments",
            filters: {
                patient: {field: 'patient', value: null},
                start: {field: 'start', value: null},
                end: {field: 'end', value: null},
                status: {field: 'status', value: null},
            },
            statusOptions: [
                {value: 'proposed', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_PROPOSED')},
                {value: 'pending', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_PENDING')},
                {value: 'booked', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_BOOKED')},
                {value: 'arrived', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_ARRIVED')},
                {value: 'fulfilled', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_FULFILLED')},
                {value: 'cancelled', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_CANCELLED')},
                {value: 'noshow', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_NOSHOW')},
                {value: 'entered-in-error', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_ERROR')},
                {value: 'checked-in', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_CHECKEDIN')},
                {value: 'waitlist', text: this.$i18n.gt('LABEL_APPOINTMENT_STATUS_WAITLIST')},
            ],
        }
    },
    computed: {
        fields() {
            return [
                {key: 'date', sortable: true, label: 'Date'},
                {key: 'start', sortable: false, label: 'Start'},
                {key: 'end', sortable: false, label: 'End'},
                {key: 'comment', sortable: false, label: 'Comment'},
                {key: 'emr_status', sortable: true, label: 'Status'},
            ]
        }
    },
    methods: {
        showModal() {
            this.$bvModal.show(this.modal_id)
        },
        hideModal() {
            this.$bvModal.hide(this.modal_id)
        },
        getAppointments(listParams) {
            let filters = this.filters
            filters.type = {field: 'type', value: 'appointment'}
            return this.$api.emrLookup(this, this.filters, listParams, {participant: this.participantId})
        },
        filtersChanged() {
            this.$refs.dataTable.debouncedRefresh()
        },
        displayStatus(status) {
            for (let statusOption of this.statusOptions)
                if (status === statusOption.value)
                    return statusOption.text;
            return status;
        },
        displayComment(comment) {
            return comment ? comment : '<em>(' + this.$i18n.gt('LABEL_NO_COMMENT') + ')</em>'
        },
        setFilters(period) {
            this.filters.start.value = moment().startOf(period).format(this.DATE_FORMAT)
            this.filters.end.value = moment().endOf(period).format(this.DATE_FORMAT)
        }
    },
    async mounted() {
        //  default start date to one month ago
        let dateStart = moment().subtract(1, 'months').format(this.DATE_FORMAT)
        this.filters.start.value = dateStart
    }
}
</script>

<style lang="scss">

.emr-appointments-modal {
    display: inline-block;
}

.emr-appointments-modal__modal {
    min-height: 400px;
}

</style>
